<script setup>
import {onMounted, ref} from "vue";
import {fadeHide, fadeShow} from "../../lib/animate/index.js";
import {useLocalStorage} from "@straylightagency/storage";
import {useCookie} from "@straylightagency/cookies";
import gdpr from "../../config/gdpr.js";
import unlessElementExists from "../../lib/utils/unlessElementExists.js";

const {privacyPolicy} = defineProps(['privacy-policy']);

const noticeEl = ref();

const categories = gdpr.categories;

function handleChoices(choices) {
    Object.entries( categories ).forEach( ([name,values]) => {
        if ( choices[ name ] === true && values.apps !== null ) {

            values.apps.forEach( app => {
                if ( gdpr.apps[ app ] ) {
                    unlessElementExists( '#' + gdpr.apps[ app ].id, el => {
                        if ( el.tagName.toLowerCase() === 'script' ) {
                            if ( el.hasAttribute('data-src') ) {
                                el.onload = () => typeof gdpr.apps[ app ].callback === 'function' && gdpr.apps[ app ].callback( el );
                                el.type = el.getAttribute('data-type');
                                el.src = el.getAttribute('data-src');
                            } else {
                                const newScriptTag = document.createElement('script');
                                newScriptTag.innerHTML = el.innerHTML;
                                typeof gdpr.apps[ app ].callback === 'function' && gdpr.apps[ app ].callback( newScriptTag );
                                el.parentNode.insertBefore( newScriptTag, el );
                                el.parentNode.removeChild( el );
                            }
                        }

                        if ( el.tagName.toLowerCase() === 'iframe' ) {
                            typeof gdpr.apps[ app ].callback === 'function' && gdpr.apps[ app ].callback( el );
                            el.src = el.getAttribute('data-src');
                        }
                    } );
                }
            } );
        }
    } );

    unlessElementExists('#cookiesModal', modal => {
        unlessElementExists(modal, '#cookiesForm', form => {
            form.querySelector('input[type=checkbox]').setAttribute('checked', false );

            Object.entries( choices ).forEach( ([name,value]) => {
                if ( value ) {
                    form.querySelector('input[type=checkbox][name='+ name +']').setAttribute('checked', true );
                }
            } );
        } );
    } );
}

const default_choices = Object.fromEntries(
    Object.entries( categories ).map( ([name,values]) => [ name, values.default ] )
);

const [cookiesNotice, setCookiesNotice] = useLocalStorage('cookies.bar', false );
const [cookiesSettings, setCookiesSettings] = useCookie('cookies_settings', default_choices );

handleChoices(
    cookiesSettings
);

function saveChoices(choices) {
    setCookiesNotice( true  );
    setCookiesSettings( choices, { secure: location.protocol === 'https:' } );

    handleChoices( choices );
}

function acceptCookies() {
    saveChoices( Object.fromEntries(
        Object.entries( categories ).map( ([name,values]) => [ name, true ] )
    ) );

    fadeHide( noticeEl.value, 300 );
}

function denyCookies() {
    saveChoices( default_choices );

    fadeHide( noticeEl.value, 300 );
}

onMounted( () => {
    if ( ! cookiesNotice.value ) {
        setTimeout( () => fadeShow( noticeEl.value, 300 ), 2000 );
    }
} );
</script>

<template>
    <div class="fixed bottom-0 right-0 p-5 z-50 hidden" id="cookiesNotice" ref="noticeEl">
        <div class="w-full max-w-80 rounded bg-white shadow-md p-2.5">
            <p class="font-bold mb-2.5">Notre site utilise des cookies</p>

            <p class="text-xs mb-2.5">
                Nous utilisons des cookies afin d'améliorer votre expérience sur notre site et de mesurer l'audience. Bonne visite !
            </p>

            <p class="text-xs mb-2.5">
                Pour plus d'informations, consultez notre <a :href="privacyPolicy" class="underline">politique en matière de cookies</a>.
            </p>

            <button type="button" title="Accepter tous les cookies" aria-label="Accepter tous les cookies" @click.prevent="acceptCookies" tabindex="-1"
                    class="block text-xs border border-green-400 rounded w-full px-5 py-2 mb-2 bg-green-400 hover:bg-green-500 transition-colors transition-150">Accepter tous les cookies</button>
            <button type="button" title="Refuser les cookies optionnels" aria-label="Refuser les cookies optionnels" @click.prevent="denyCookies" tabindex="-1"
                    class="block text-xs border border-red-400 rounded w-full px-5 py-2 mb-2 bg-red-400 hover:bg-red-500 text-white transition-colors transition-150">Refuser les cookies optionnels</button>
            <button type="button" title="Paramétrer les cookies" aria-label="Paramétrer les cookies" ty-modal="#cookiesModal" tabindex="-1"
                    class="block text-xs border rounded w-full px-5 py-2 bg-transparent hover:bg-slate-200 transition-colors transition-150">Paramétrer les cookies</button>
        </div>
    </div>
</template>